import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, MessageCircle, Ban, Flag, MapPin, Clock, 
  Ruler, Scale, User, Heart, Sparkles, Lock, ChevronLeft, ChevronRight, Zap,
  Shield, Briefcase, Users, Wine, Cigarette, Cannabis, Globe, HelpCircle,
  Mic, Play, Pause, MapPinned, Eye, Unlock, LockOpen, ImageOff, MessageSquare
} from 'lucide-react';
import BottomNav from '../components/BottomNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sampleImages = [
  'https://images.unsplash.com/photo-1635631191842-cc5043d56fc4?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
  'https://images.unsplash.com/photo-1762708546140-7aea0b34ecac?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
  'https://images.unsplash.com/photo-1582439170934-d089aa10abda?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
  'https://images.unsplash.com/photo-1635631191694-8fc637d53429?crop=entropy&cs=srgb&fm=jpg&q=85&w=600'
];

const Profile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showPhotoViewer, setShowPhotoViewer] = useState(false);
  const [revealedPrivatePhotos, setRevealedPrivatePhotos] = useState(new Set());
  const [tapping, setTapping] = useState(false);
  const [favoriting, setFavoriting] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [hasPrivateAccess, setHasPrivateAccess] = useState(false);
  const [privatePhotoCount, setPrivatePhotoCount] = useState(0);
  const [grantingAccess, setGrantingAccess] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchProfile();
    checkPrivateAccess();
  }, [userId]);

  const checkPrivateAccess = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}/private-photos`);
      setHasPrivateAccess(response.data.has_access);
      setPrivatePhotoCount(response.data.photo_count);
      if (response.data.has_access && response.data.private_photos) {
        // Auto-reveal private photos if access granted
        const indices = new Set();
        response.data.private_photos.forEach((_, i) => indices.add(i));
        setRevealedPrivatePhotos(indices);
      }
    } catch (error) {
      console.error('Failed to check private access:', error);
    }
  };

  const requestPrivateAccess = async () => {
    try {
      const response = await axios.post(`${API}/users/private-access/request/${userId}`);
      if (response.data.already_has_access) {
        toast.info('You already have access to their private photos!');
      } else if (response.data.already_requested) {
        toast.info('Request already sent - they\'ll see it in their notifications');
        navigate(`/chat/${userId}`);
      } else {
        toast.success('Request sent! They\'ll be notified');
        navigate(`/chat/${userId}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
      navigate(`/chat/${userId}`);
    }
  };

  const grantPrivateAccess = async () => {
    if (grantingAccess) return;
    setGrantingAccess(true);
    try {
      await axios.post(`${API}/users/private-access/grant/${userId}`);
      toast.success(`You shared your private photos with ${profile.display_name || profile.username}`);
      setHasPrivateAccess(true); // Update UI to reflect access granted
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to grant access');
    } finally {
      setGrantingAccess(false);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/${userId}`);
      setProfile(response.data);
    } catch (error) {
      toast.error('Failed to load profile');
      navigate('/grid');
    } finally {
      setLoading(false);
    }
  };

  const handleMessage = async () => {
    navigate(`/chat/${userId}`);
  };

  const handleTap = async () => {
    if (tapping) return;
    setTapping(true);
    try {
      if (profile.i_tapped) {
        await axios.delete(`${API}/users/tap/${userId}`);
        setProfile(p => ({ ...p, i_tapped: false }));
        toast.success('Tap removed');
      } else {
        const response = await axios.post(`${API}/users/tap`, { user_id: userId });
        setProfile(p => ({ ...p, i_tapped: true }));
        if (response.data.is_mutual) {
          toast.success("It's a match! You both tapped each other! 🎉");
        } else {
          toast.success('Tap sent!');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to tap');
    } finally {
      setTapping(false);
    }
  };

  const handleFavorite = async () => {
    if (favoriting) return;
    setFavoriting(true);
    try {
      if (profile.is_favorite) {
        await axios.delete(`${API}/users/favorites/${userId}`);
        setProfile(p => ({ ...p, is_favorite: false }));
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/users/favorites`, { user_id: userId });
        setProfile(p => ({ ...p, is_favorite: true }));
        toast.success('Added to favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    } finally {
      setFavoriting(false);
    }
  };

  const handleBlock = async () => {
    try {
      await axios.post(`${API}/users/block`, { user_id: userId });
      toast.success('User blocked. They can no longer see you or contact you.');
      setShowBlockModal(false);
      navigate('/grid');
    } catch (error) {
      toast.error('Failed to block user');
    }
  };

  const handleReport = async () => {
    if (!reportReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    try {
      await axios.post(`${API}/users/report`, { user_id: userId, reason: reportReason });
      toast.success('Report submitted');
      setShowReportModal(false);
      setReportReason('');
    } catch (error) {
      toast.error('Failed to submit report');
    }
  };

  const getProfileImage = (index = 0) => {
    if (profile?.public_photos && profile.public_photos.length > index) {
      const photo = profile.public_photos[index];
      if (photo.startsWith('data:') || photo.startsWith('http')) {
        return photo;
      }
      return `data:image/jpeg;base64,${photo}`;
    }
    return sampleImages[index % sampleImages.length];
  };

  const getAllPhotos = () => {
    const photos = [];
    if (profile?.public_photos) {
      profile.public_photos.forEach((photo, i) => {
        photos.push({ type: 'public', photo, index: i });
      });
    }
    // Only show private photos if access is granted
    if (hasPrivateAccess && profile?.private_photos) {
      profile.private_photos.forEach((photo, i) => {
        photos.push({ type: 'private', photo, index: i, unlocked: true });
      });
    } else if (privatePhotoCount > 0) {
      // Show locked placeholders for private photos count
      for (let i = 0; i < privatePhotoCount; i++) {
        photos.push({ type: 'private', photo: null, index: i, unlocked: false });
      }
    }
    if (photos.length === 0) {
      return sampleImages.slice(0, 3).map((photo, i) => ({ type: 'public', photo, index: i }));
    }
    return photos;
  };

  const togglePrivatePhoto = (index) => {
    // Only allow reveal if access is granted
    if (!hasPrivateAccess) {
      requestPrivateAccess();
      return;
    }
    setRevealedPrivatePhotos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const formatLastActive = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!profile) return null;

  const allPhotos = getAllPhotos();

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="flex items-center justify-between px-4 py-3">
          <Button
            data-testid="back-btn"
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <Button
              data-testid="report-btn"
              variant="ghost"
              size="icon"
              onClick={() => setShowReportModal(true)}
              className="text-[#A1A1AA] hover:text-white hover:bg-white/10"
            >
              <Flag className="w-5 h-5" />
            </Button>
            <Button
              data-testid="block-btn"
              variant="ghost"
              size="icon"
              onClick={() => setShowBlockModal(true)}
              className="text-[#A1A1AA] hover:text-white hover:bg-white/10"
            >
              <Ban className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Photo */}
      <div 
        className="relative w-full aspect-[3/4] max-h-[60vh] cursor-pointer"
        onClick={() => setShowPhotoViewer(true)}
      >
        <img
          src={getProfileImage(0)}
          alt={profile.display_name || profile.username}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050505]" />
        
        {/* Online indicator */}
        {profile.is_online && (
          <div className="absolute top-16 right-4 flex items-center gap-2 glass px-3 py-1.5 rounded-full">
            <div className="online-indicator w-2 h-2" />
            <span className="text-xs text-[#08D9D6]">Online</span>
          </div>
        )}

        {/* Tap indicator if they tapped you */}
        {profile.has_tapped_me && (
          <div className="absolute top-16 left-4 flex items-center gap-2 glass px-3 py-1.5 rounded-full bg-[#FF2E63]/80">
            <Zap className="w-4 h-4 text-white" />
            <span className="text-xs text-white font-semibold">Tapped You!</span>
          </div>
        )}

        {/* Basic info overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <h1 className="text-3xl font-bold text-white">
            {profile.display_name || profile.username}
            {profile.age && <span className="font-normal">, {profile.age}</span>}
          </h1>
          <div className="flex items-center gap-4 mt-2 text-[#A1A1AA]">
            {profile.distance !== null && profile.distance !== undefined && (
              <span className="flex items-center gap-1 text-sm font-mono">
                <MapPin className="w-4 h-4" />
                {profile.distance < 1 ? '< 1' : profile.distance} km
              </span>
            )}
            {!profile.is_online && (
              <span className="flex items-center gap-1 text-sm">
                <Clock className="w-4 h-4" />
                {formatLastActive(profile.last_active)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Profile Content */}
      <div className="px-4 py-6 space-y-6">
        {/* Action Buttons - Icon Row */}
        <div className="flex justify-center gap-6">
          <Button
            data-testid="tap-btn"
            onClick={handleTap}
            disabled={tapping}
            size="icon"
            className={`w-16 h-16 rounded-full transition-all ${
              profile.i_tapped 
                ? 'bg-[#FF2E63] text-white shadow-lg shadow-[#FF2E63]/30' 
                : 'bg-white/10 text-[#FF2E63] border-2 border-[#FF2E63]/50 hover:bg-[#FF2E63]/20'
            }`}
          >
            <Zap className={`w-7 h-7 ${profile.i_tapped ? 'fill-current' : ''}`} />
          </Button>
          
          <Button
            data-testid="message-btn"
            onClick={handleMessage}
            size="icon"
            className="w-16 h-16 rounded-full bg-[#FF2E63] hover:bg-[#E01E50] text-white shadow-lg shadow-[#FF2E63]/30"
          >
            <MessageCircle className="w-7 h-7" />
          </Button>
          
          <Button
            data-testid="favorite-btn"
            onClick={handleFavorite}
            disabled={favoriting}
            size="icon"
            className={`w-16 h-16 rounded-full transition-all ${
              profile.is_favorite 
                ? 'bg-[#FF2E63] text-white shadow-lg shadow-[#FF2E63]/30' 
                : 'bg-white/10 text-[#A1A1AA] border-2 border-white/20 hover:text-[#FF2E63] hover:border-[#FF2E63]/50'
            }`}
          >
            <Heart className={`w-7 h-7 ${profile.is_favorite ? 'fill-current' : ''}`} />
          </Button>
        </div>

        {/* Action Labels */}
        <div className="flex justify-center gap-6 -mt-2">
          <span className={`w-16 text-center text-xs ${profile.i_tapped ? 'text-[#FF2E63]' : 'text-[#A1A1AA]'}`}>
            {profile.i_tapped ? 'Tapped' : 'Tap'}
          </span>
          <span className="w-16 text-center text-xs text-[#A1A1AA]">Message</span>
          <span className={`w-16 text-center text-xs ${profile.is_favorite ? 'text-[#FF2E63]' : 'text-[#A1A1AA]'}`}>
            {profile.is_favorite ? 'Saved' : 'Save'}
          </span>
        </div>

        {/* Availability Status Badge */}
        {profile.availability_status && profile.availability_status !== 'just_browsing' && (
          <div 
            data-testid="availability-badge"
            className={`flex items-center justify-center gap-2 py-3 rounded-full ${
              profile.availability_status === 'available_now'
                ? 'bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/30'
                : 'bg-[#08D9D6]/20 text-[#08D9D6] border border-[#08D9D6]/30'
            }`}
          >
            {profile.availability_status === 'available_now' ? (
              <>
                <Zap className="w-5 h-5" />
                <span className="font-semibold">Available Now</span>
              </>
            ) : (
              <>
                <MapPinned className="w-5 h-5" />
                <span className="font-semibold">Ready to Meet</span>
              </>
            )}
          </div>
        )}

        {/* Local Guide Badge */}
        {profile.is_local_guide && (
          <div 
            data-testid="local-guide-badge"
            className="flex items-center justify-center gap-2 py-3 rounded-full bg-[#FFD700]/20 text-[#FFD700] border border-[#FFD700]/30"
          >
            <MapPinned className="w-5 h-5" />
            <span className="font-semibold">Local Guide</span>
          </div>
        )}

        {/* Vibe Check - Voice Intro */}
        {profile.voice_intro && (
          <div className="glass rounded-xl p-4" data-testid="vibe-check-section">
            <h3 className="text-sm font-semibold text-[#A1A1AA] mb-3 flex items-center gap-2">
              <Mic className="w-4 h-4 text-[#FF2E63]" />
              Vibe Check
            </h3>
            <div className="flex items-center gap-3">
              <Button
                data-testid="play-voice-intro"
                onClick={() => {
                  if (audioRef.current) {
                    if (isPlayingAudio) {
                      audioRef.current.pause();
                      setIsPlayingAudio(false);
                    } else {
                      audioRef.current.play();
                      setIsPlayingAudio(true);
                    }
                  }
                }}
                className={`w-12 h-12 rounded-full ${
                  isPlayingAudio 
                    ? 'bg-[#FF2E63] text-white' 
                    : 'bg-white/10 text-[#FF2E63] hover:bg-white/20'
                }`}
              >
                {isPlayingAudio ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
              </Button>
              <div className="flex-1">
                <p className="text-[#EDEDED] text-sm">Listen to {profile.display_name || profile.username}&apos;s voice</p>
                <p className="text-xs text-[#52525B]">30 sec intro</p>
              </div>
              <audio 
                ref={audioRef}
                src={profile.voice_intro}
                onEnded={() => setIsPlayingAudio(false)}
                className="hidden"
              />
            </div>
          </div>
        )}

        {/* Icebreaker Prompts */}
        {(profile.icebreaker_1 || profile.icebreaker_2 || profile.icebreaker_3) && (
          <div className="glass rounded-xl p-4 space-y-3" data-testid="icebreakers-section">
            <h3 className="text-sm font-semibold text-[#A1A1AA] mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-[#08D9D6]" />
              Get to Know Me
            </h3>
            {profile.icebreaker_prompt_1 && profile.icebreaker_1 && (
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-xs text-[#08D9D6] mb-1">{profile.icebreaker_prompt_1}</p>
                <p className="text-[#EDEDED]">{profile.icebreaker_1}</p>
              </div>
            )}
            {profile.icebreaker_prompt_2 && profile.icebreaker_2 && (
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-xs text-[#08D9D6] mb-1">{profile.icebreaker_prompt_2}</p>
                <p className="text-[#EDEDED]">{profile.icebreaker_2}</p>
              </div>
            )}
            {profile.icebreaker_prompt_3 && profile.icebreaker_3 && (
              <div className="bg-white/5 rounded-lg p-3">
                <p className="text-xs text-[#08D9D6] mb-1">{profile.icebreaker_prompt_3}</p>
                <p className="text-[#EDEDED]">{profile.icebreaker_3}</p>
              </div>
            )}
          </div>
        )}

        {/* Bio */}
        {profile.bio && (
          <div className="glass rounded-xl p-4">
            <h3 className="text-sm font-semibold text-[#A1A1AA] mb-2">About</h3>
            <p className="text-[#EDEDED] whitespace-pre-wrap">{profile.bio}</p>
          </div>
        )}

        {/* Stats Grid */}
        <div className="stats-grid">
          {profile.height && (
            <div className="glass rounded-lg p-3">
              <div className="flex items-center gap-2 text-[#A1A1AA] mb-1">
                <Ruler className="w-4 h-4" />
                <span className="text-xs">Height</span>
              </div>
              <p className="text-[#EDEDED] font-semibold">{profile.height}</p>
            </div>
          )}
          {profile.weight && (
            <div className="glass rounded-lg p-3">
              <div className="flex items-center gap-2 text-[#A1A1AA] mb-1">
                <Scale className="w-4 h-4" />
                <span className="text-xs">Weight</span>
              </div>
              <p className="text-[#EDEDED] font-semibold">{profile.weight}</p>
            </div>
          )}
          {profile.body_type && (
            <div className="glass rounded-lg p-3">
              <div className="flex items-center gap-2 text-[#A1A1AA] mb-1">
                <User className="w-4 h-4" />
                <span className="text-xs">Body Type</span>
              </div>
              <p className="text-[#EDEDED] font-semibold capitalize">{profile.body_type}</p>
            </div>
          )}
          {profile.position && (
            <div className="glass rounded-lg p-3">
              <div className="flex items-center gap-2 text-[#A1A1AA] mb-1">
                <Heart className="w-4 h-4" />
                <span className="text-xs">Position</span>
              </div>
              <p className="text-[#EDEDED] font-semibold capitalize">{profile.position}</p>
            </div>
          )}
          {profile.ethnicity && (
            <div className="glass rounded-lg p-3">
              <div className="flex items-center gap-2 text-[#A1A1AA] mb-1">
                <Sparkles className="w-4 h-4" />
                <span className="text-xs">Ethnicity</span>
              </div>
              <p className="text-[#EDEDED] font-semibold capitalize">{profile.ethnicity}</p>
            </div>
          )}
        </div>

        {/* Looking For */}
        {profile.looking_for && profile.looking_for.length > 0 && (
          <div className="glass rounded-xl p-4">
            <h3 className="text-sm font-semibold text-[#A1A1AA] mb-3">Looking For</h3>
            <div className="flex flex-wrap gap-2">
              {profile.looking_for.map((item, i) => (
                <span 
                  key={i}
                  className="px-3 py-1.5 bg-[#FF2E63]/20 text-[#FF2E63] rounded-full text-sm capitalize"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Interests */}
        {profile.interests && profile.interests.length > 0 && (
          <div className="glass rounded-xl p-4">
            <h3 className="text-sm font-semibold text-[#A1A1AA] mb-3">Interests</h3>
            <div className="flex flex-wrap gap-2">
              {profile.interests.map((item, i) => (
                <span 
                  key={i}
                  className="px-3 py-1.5 bg-white/10 text-[#EDEDED] rounded-full text-sm capitalize"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Health & Safety */}
        {(profile.hiv_status || profile.last_tested || profile.covid_vaccination || profile.safer_sex) && (
          <div className="glass rounded-xl p-4" data-testid="health-section">
            <h3 className="text-sm font-semibold text-[#A1A1AA] mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4 text-[#08D9D6]" />
              Health & Safety
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {profile.hiv_status && (
                <div className="bg-white/5 rounded-lg p-3">
                  <span className="text-xs text-[#A1A1AA] block mb-1">HIV Status</span>
                  <span className="text-[#EDEDED] text-sm">{profile.hiv_status}</span>
                </div>
              )}
              {profile.last_tested && (
                <div className="bg-white/5 rounded-lg p-3">
                  <span className="text-xs text-[#A1A1AA] block mb-1">Last Tested</span>
                  <span className="text-[#EDEDED] text-sm">{profile.last_tested}</span>
                </div>
              )}
              {profile.covid_vaccination && (
                <div className="bg-white/5 rounded-lg p-3">
                  <span className="text-xs text-[#A1A1AA] block mb-1">COVID Vaccination</span>
                  <span className="text-[#EDEDED] text-sm">{profile.covid_vaccination}</span>
                </div>
              )}
              {profile.safer_sex && (
                <div className="bg-white/5 rounded-lg p-3">
                  <span className="text-xs text-[#A1A1AA] block mb-1">Safer Sex</span>
                  <span className="text-[#EDEDED] text-sm">{profile.safer_sex}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Connection & Lifestyle */}
        {(profile.relationship_status || profile.pronouns || profile.occupation || profile.meet_at || 
          profile.smoking || profile.drinking || profile.cannabis || 
          (profile.languages && profile.languages.length > 0)) && (
          <div className="glass rounded-xl p-4" data-testid="lifestyle-section">
            <h3 className="text-sm font-semibold text-[#A1A1AA] mb-3 flex items-center gap-2">
              <Heart className="w-4 h-4 text-[#FF2E63]" />
              Connection & Lifestyle
            </h3>
            <div className="space-y-3">
              {/* Primary info row */}
              <div className="grid grid-cols-2 gap-3">
                {profile.relationship_status && (
                  <div className="bg-white/5 rounded-lg p-3">
                    <span className="text-xs text-[#A1A1AA] block mb-1">Relationship</span>
                    <span className="text-[#EDEDED] text-sm">{profile.relationship_status}</span>
                  </div>
                )}
                {profile.pronouns && (
                  <div className="bg-white/5 rounded-lg p-3">
                    <span className="text-xs text-[#A1A1AA] block mb-1">Pronouns</span>
                    <span className="text-[#EDEDED] text-sm">{profile.pronouns}</span>
                  </div>
                )}
                {profile.occupation && (
                  <div className="bg-white/5 rounded-lg p-3 flex items-start gap-2">
                    <Briefcase className="w-4 h-4 text-[#A1A1AA] mt-0.5 shrink-0" />
                    <div>
                      <span className="text-xs text-[#A1A1AA] block mb-1">Occupation</span>
                      <span className="text-[#EDEDED] text-sm">{profile.occupation}</span>
                    </div>
                  </div>
                )}
                {profile.meet_at && (
                  <div className="bg-white/5 rounded-lg p-3">
                    <span className="text-xs text-[#A1A1AA] block mb-1">Meet At</span>
                    <span className="text-[#EDEDED] text-sm">{profile.meet_at}</span>
                  </div>
                )}
              </div>

              {/* Substances row */}
              {(profile.smoking || profile.drinking || profile.cannabis) && (
                <div className="flex flex-wrap gap-2">
                  {profile.smoking && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-full">
                      <Cigarette className="w-4 h-4 text-[#A1A1AA]" />
                      <span className="text-sm text-[#EDEDED]">{profile.smoking}</span>
                    </div>
                  )}
                  {profile.drinking && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-full">
                      <Wine className="w-4 h-4 text-[#A1A1AA]" />
                      <span className="text-sm text-[#EDEDED]">{profile.drinking}</span>
                    </div>
                  )}
                  {profile.cannabis && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-full">
                      <Cannabis className="w-4 h-4 text-[#A1A1AA]" />
                      <span className="text-sm text-[#EDEDED]">{profile.cannabis}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Languages */}
              {profile.languages && profile.languages.length > 0 && (
                <div className="bg-white/5 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Globe className="w-4 h-4 text-[#A1A1AA]" />
                    <span className="text-xs text-[#A1A1AA]">Languages</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {profile.languages.map((lang, i) => (
                      <span 
                        key={i}
                        className="px-2 py-1 bg-[#08D9D6]/10 text-[#08D9D6] rounded text-xs"
                      >
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Photo Gallery */}
        {allPhotos.length > 0 && (
          <div className="glass rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-[#A1A1AA]">Photos</h3>
              {privatePhotoCount > 0 && !hasPrivateAccess && (
                <span className="text-xs text-[#FF2E63] flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  {privatePhotoCount} private
                </span>
              )}
            </div>
            <div className="photo-gallery">
              {allPhotos.map((photoItem, i) => (
                <div 
                  key={i}
                  data-testid={`photo-${i}`}
                  className="relative aspect-square cursor-pointer overflow-hidden rounded-md"
                  onClick={() => {
                    if (photoItem.type === 'private' && !photoItem.unlocked) {
                      requestPrivateAccess();
                    } else if (photoItem.photo) {
                      setCurrentPhotoIndex(i);
                      setShowPhotoViewer(true);
                    }
                  }}
                >
                  {photoItem.photo ? (
                    <img
                      src={photoItem.photo.startsWith('data:') || photoItem.photo.startsWith('http') 
                        ? photoItem.photo 
                        : `data:image/jpeg;base64,${photoItem.photo}`}
                      alt={`Photo ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] flex items-center justify-center">
                      <ImageOff className="w-8 h-8 text-[#52525B]" />
                    </div>
                  )}
                  {photoItem.type === 'private' && !photoItem.unlocked && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 backdrop-blur-sm">
                      <Lock className="w-6 h-6 text-[#FF2E63] mb-1" />
                      <span className="text-xs text-[#A1A1AA]">Private</span>
                    </div>
                  )}
                  {photoItem.type === 'private' && photoItem.unlocked && (
                    <div className="absolute top-1 right-1">
                      <Unlock className="w-4 h-4 text-[#08D9D6] drop-shadow-md" />
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            {/* Request/Unlock Access Banner */}
            {privatePhotoCount > 0 && !hasPrivateAccess && (
              <button
                data-testid="request-private-access-btn"
                onClick={requestPrivateAccess}
                className="mt-3 w-full py-2.5 px-4 bg-[#FF2E63]/10 border border-[#FF2E63]/30 rounded-lg flex items-center justify-center gap-2 text-[#FF2E63] hover:bg-[#FF2E63]/20 transition-colors"
              >
                <Lock className="w-4 h-4" />
                <span className="text-sm font-medium">Request Access to Private Photos</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Photo Viewer Modal */}
      <Dialog open={showPhotoViewer} onOpenChange={setShowPhotoViewer}>
        <DialogContent className="bg-black border-none max-w-4xl h-[90vh] p-0">
          <div className="relative w-full h-full flex items-center justify-center">
            <img
              src={allPhotos[currentPhotoIndex]?.photo?.startsWith('data:') || allPhotos[currentPhotoIndex]?.photo?.startsWith('http')
                ? allPhotos[currentPhotoIndex]?.photo
                : `data:image/jpeg;base64,${allPhotos[currentPhotoIndex]?.photo}`}
              alt={`Photo ${currentPhotoIndex + 1}`}
              className="max-w-full max-h-full object-contain"
            />
            
            {/* Navigation */}
            {allPhotos.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCurrentPhotoIndex(prev => prev === 0 ? allPhotos.length - 1 : prev - 1)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
                >
                  <ChevronLeft className="w-6 h-6" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setCurrentPhotoIndex(prev => prev === allPhotos.length - 1 ? 0 : prev + 1)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white"
                >
                  <ChevronRight className="w-6 h-6" />
                </Button>
              </>
            )}

            {/* Photo counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 glass px-4 py-2 rounded-full">
              <span className="text-sm text-white">
                {currentPhotoIndex + 1} / {allPhotos.length}
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Block Modal */}
      <Dialog open={showBlockModal} onOpenChange={setShowBlockModal}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Block User</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Are you sure you want to block {profile.display_name || profile.username}? They will not be able to see your profile or message you.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-3">
            <Button
              data-testid="cancel-block-btn"
              variant="ghost"
              onClick={() => setShowBlockModal(false)}
              className="text-[#A1A1AA] hover:text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-block-btn"
              onClick={handleBlock}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Block User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Report Modal */}
      <Dialog open={showReportModal} onOpenChange={setShowReportModal}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Report User</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              Please describe why you are reporting this user.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            data-testid="report-reason-input"
            value={reportReason}
            onChange={(e) => setReportReason(e.target.value)}
            placeholder="Describe the issue..."
            className="bg-white/5 border-white/10 text-white placeholder:text-white/30 min-h-[120px]"
          />
          <DialogFooter className="gap-3">
            <Button
              data-testid="cancel-report-btn"
              variant="ghost"
              onClick={() => setShowReportModal(false)}
              className="text-[#A1A1AA] hover:text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              data-testid="submit-report-btn"
              onClick={handleReport}
              className="bg-[#FF2E63] hover:bg-[#E01E50] text-white"
            >
              Submit Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BottomNav />
    </div>
  );
};

export default Profile;
